import { Anchor, Box, Button, Group, Modal, Popover, Select, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { IconFilter, IconRadar2, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { CitySelect } from "../CitySelect";
import { EmptyState, LoadingOverlay } from "../ui";
import { jobsApi } from "../../lib/api";
import { formatCities } from "../../lib/format";
import { catalogFuzzyFilter } from "../../lib/catalogFuzzyFilter";
import { useSegmentCatalog } from "../../lib/useSegmentCatalog";
import { renderSegmentOption } from "../SegmentOptionLabel";
import type { Job } from "../../types/api";
import { axioTokens } from "../../theme";
import { EXPLORE_JOB_PICKER_PAGE_SIZE, EXPLORE_WORK_MODE_OPTIONS, POPOVER_CLOSE_STYLE } from "./constants";
import { youEarnLabel } from "./exploreUtils";

export const JOB_PICKER_TITLE = "Choose an open job";
export const JOB_PICKER_SEARCH_LABEL = "Search";
export const JOB_PICKER_SEARCH_PLACEHOLDER = "Job title";
export const JOB_PICKER_FILTERS_LABEL = "Filters";
export const JOB_PICKER_CLEAR_FILTERS_LABEL = "Clear filters";
export const JOB_PICKER_CANCEL_LABEL = "Cancel";
export const JOB_PICKER_SEGMENT_LABEL = "Segment";
export const JOB_PICKER_CITY_LABEL = "City";
export const JOB_PICKER_CITY_PLACEHOLDER = "Any city";
export const JOB_PICKER_WORK_MODE_LABEL = "Work mode";

export function JobPickerModal({
  opened,
  onClose,
  onPick,
}: {
  opened: boolean;
  onClose: () => void;
  onPick: (job: Job) => void;
}) {
  const [jobSearch, setJobSearch] = useState("");
  const [jobSegment, setJobSegment] = useState<string | null>(null);
  const [jobCity, setJobCity] = useState("");
  const [jobWorkMode, setJobWorkMode] = useState<string | null>(null);
  const [jobPickerPage, setJobPickerPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { flatOptions: segmentOptions } = useSegmentCatalog();

  const hasExtraFilters = Boolean(jobSegment || jobCity || jobWorkMode);

  useEffect(() => {
    if (!opened) setFiltersOpen(false);
  }, [opened]);

  function clearFilters() {
    setJobSegment(null);
    setJobCity("");
    setJobWorkMode(null);
    setJobPickerPage(1);
  }

  const pickerJobsQuery = useQuery({
    queryKey: ["explore-job-picker", jobSearch, jobSegment, jobCity, jobWorkMode, jobPickerPage],
    queryFn: () => jobsApi.list({
      q: jobSearch || undefined,
      segment: jobSegment || undefined,
      city: jobCity || undefined,
      work_mode: jobWorkMode || undefined,
      page: jobPickerPage,
      page_size: EXPLORE_JOB_PICKER_PAGE_SIZE,
    }),
    enabled: opened,
  });
  const pickerCitiesQuery = useQuery({
    queryKey: ["explore-job-picker-cities", jobSegment, jobWorkMode],
    queryFn: () =>
      jobsApi.availableCities({
        segment: jobSegment || undefined,
        work_mode: jobWorkMode || undefined,
      }),
    enabled: opened,
  });
  const pickerCities = pickerCitiesQuery.data ?? [];

  const total = pickerJobsQuery.data?.total ?? 0;

  return (
    <Modal opened={opened} onClose={onClose} title={JOB_PICKER_TITLE} size="xl">
      <Stack gap="lg">
        <Group wrap="nowrap" align="flex-end" gap="md">
          <TextInput
            label={JOB_PICKER_SEARCH_LABEL}
            placeholder={JOB_PICKER_SEARCH_PLACEHOLDER}
            value={jobSearch}
            onChange={(event) => {
              setJobSearch(event.currentTarget.value);
              setJobPickerPage(1);
            }}
            style={{ flex: axioTokens.number.one, minWidth: axioTokens.unit[0] }}
          />
          <Popover opened={filtersOpen} onChange={setFiltersOpen} position="bottom-end" withinPortal>
            <Popover.Target>
              <Button
                variant={filtersOpen || hasExtraFilters ? "filled" : "default"}
                color={filtersOpen || hasExtraFilters ? "violet" : undefined}
                leftSection={<IconFilter size="var(--axio-unit-16)" />}
                aria-expanded={filtersOpen}
                aria-label={JOB_PICKER_FILTERS_LABEL}
                onClick={(event) => {
                  event.stopPropagation();
                  setFiltersOpen((open) => !open);
                }}
              >
                {JOB_PICKER_FILTERS_LABEL}
              </Button>
            </Popover.Target>
            <Popover.Dropdown p="md" w="var(--axio-unit-320)">
              <Box pos="relative">
                <Anchor
                  href="#"
                  c="dimmed"
                  display="inline-flex"
                  underline="never"
                  aria-label={JOB_PICKER_CANCEL_LABEL}
                  style={POPOVER_CLOSE_STYLE}
                  onClick={(event) => {
                    event.preventDefault();
                    setFiltersOpen(false);
                  }}
                >
                  <IconX size="var(--axio-unit-14)" />
                </Anchor>
                <Stack gap="md">
                <Select
                  label={JOB_PICKER_SEGMENT_LABEL}
                  data={segmentOptions}
                  value={jobSegment}
                  onChange={(value) => {
                    setJobSegment(value);
                    setJobCity("");
                    setJobPickerPage(1);
                  }}
                  clearable
                  searchable
                  renderOption={renderSegmentOption}
                  filter={catalogFuzzyFilter}
                  comboboxProps={{ withinPortal: true }}
                />
                <CitySelect
                  label={JOB_PICKER_CITY_LABEL}
                  placeholder={JOB_PICKER_CITY_PLACEHOLDER}
                  flat
                  value={jobCity}
                  onChange={(value) => {
                    setJobCity(value);
                    setJobPickerPage(1);
                  }}
                  cities={pickerCities}
                  comboboxProps={{ withinPortal: true }}
                />
                <Select
                  label={JOB_PICKER_WORK_MODE_LABEL}
                  data={EXPLORE_WORK_MODE_OPTIONS}
                  value={jobWorkMode}
                  onChange={(value) => {
                    setJobWorkMode(value);
                    setJobCity("");
                    setJobPickerPage(1);
                  }}
                  clearable
                  comboboxProps={{ withinPortal: true }}
                />
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  underline="always"
                  disabled={!hasExtraFilters}
                  onClick={clearFilters}
                >
                  {JOB_PICKER_CLEAR_FILTERS_LABEL}
                </Anchor>
              </Stack>
              </Box>
            </Popover.Dropdown>
          </Popover>
        </Group>
        {pickerJobsQuery.isLoading ? <LoadingOverlay /> : pickerJobsQuery.isError ? (
          <EmptyState icon={IconRadar2} title="Could not load jobs" description="Please retry." action={<Button onClick={() => void pickerJobsQuery.refetch()}>Retry</Button>} />
        ) : (
          <Stack gap="sm">
            {(pickerJobsQuery.data?.items ?? []).map((job) => {
              const openingsLeft = job.openings_total - job.openings_filled;
              return (
                <UnstyledButton
                  key={job.id}
                  onClick={() => {
                    onPick(job);
                    onClose();
                  }}
                  w={axioTokens.percentage.full}
                  p="md"
                  style={{
                    border: `${axioTokens.unit[1]}px solid ${axioTokens.border}`,
                    borderRadius: "var(--axio-radius)",
                    background: axioTokens.surface,
                    textAlign: "left",
                  }}
                >
                  <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                    <Stack gap="var(--axio-unit-4)" style={{ flex: "1 1 auto" }}>
                      <Text fw="var(--axio-font-weight-bold)" lh="var(--axio-line-height-heading)" lineClamp={2}>
                        {job.title}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {formatCities(job.cities)}
                        {job.is_remote ? " · Remote" : ""}
                        {" · "}
                        {openingsLeft} left
                        {" · "}
                        {job.guarantee_days} day guarantee
                      </Text>
                    </Stack>
                    <Stack gap="var(--axio-unit-4)" align="flex-end" style={{ flex: "0 0 auto" }}>
                      <Text size="sm" className="moneyAmount">
                        ≈ {youEarnLabel(job)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Approx.
                      </Text>
                    </Stack>
                  </Group>
                </UnstyledButton>
              );
            })}
            {(pickerJobsQuery.data?.items ?? []).length === 0 && <Text c="dimmed">No open jobs match these filters.</Text>}
            {total > EXPLORE_JOB_PICKER_PAGE_SIZE && (
              <Group justify="space-between">
                <Button variant="default" disabled={jobPickerPage === 1} onClick={() => setJobPickerPage((page) => page - 1)}>Previous</Button>
                <Text size="sm" c="dimmed">Page {jobPickerPage} of {Math.ceil(total / EXPLORE_JOB_PICKER_PAGE_SIZE)}</Text>
                <Button variant="default" disabled={jobPickerPage * EXPLORE_JOB_PICKER_PAGE_SIZE >= total} onClick={() => setJobPickerPage((page) => page + 1)}>Next</Button>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
